import axios from "axios";
import { config } from '../../config/Config';

export const getAllRoles = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/role/org`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching roles:', error.response?.data || error.message);
        throw error;
    }
};

// A role's Admin Settings access as the built-in role definitions grant it (read-only: no edit or override changes it).
// { roleId, source: BUILT_IN | CUSTOM | MARKER, pages: [{ pageId, level, components: [{ componentId, level, ownersOnly }] }] }
export const getBuiltInAccess = async ({ roleId, accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/role/${encodeURIComponent(roleId)}/built-in-access`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching built-in access:', error.response?.data || error.message);
        throw error;
    }
};

// Roles the current user may ASSIGN at the given scope (ORG | GAME), filtered by their
// authority with no privilege escalation. Use this for invite / access-request / access-provision
// role pickers (instead of getAllRoles, which returns the full catalogue for role management).
export const getAssignableRoles = async ({ accessToken, scope = 'GAME', governanceOnly = false }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/role/assignable?scope=${encodeURIComponent(scope)}`
                + (governanceOnly ? '&governanceOnly=true' : ''),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching assignable roles:', error.response?.data || error.message);
        throw error;
    }
};

// Provision / promote an EXISTING user's access at a scope (ORG | GAME). orgId + actor
// are resolved server-side from the auth context. Grants the role and prunes now-redundant
// lower-scope grants (see AccessProvisioningService). Use the invitation flow for off-platform users.
// `geographySegmentIds` (GAME scope): the user's game access BECOMES those geographies - no whole-game grant.
export const provisionAccess = async ({ accessToken, scope, targetUserId, gameId, role, geographySegmentIds }) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/access/provision`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: { scope, targetUserId, gameId, role, geographySegmentIds }
        });
        return response.data;
    } catch (error) {
        console.error('Error provisioning access:', error.response?.data || error.message);
        throw error;
    }
};

// Change a user's whole-game role. `role` is REQUIRED: the backend treats a blank role as
// "revoke every grant on this game" (AccessProvisioningService#changeGameRole), so we refuse to
// send one. To scope a role to one geography use provisionGeographyRole instead.
export const changeUserRole = async ({ accessToken, targetUserId, gameId, role }) => {
    if (!role || !String(role).trim()) {
        throw new Error('changeUserRole requires a role; use revoke to remove access.');
    }
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/access/change-role`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: { targetUserId, gameId, role }
        });
        return response.data;
    } catch (error) {
        console.error('Error changing user role:', error.response?.data || error.message);
        throw error;
    }
};

// Remove ALL of a user's access on a game — the whole-game role AND every geography grant on it.
// The backend models this as change-role with a blank role (AccessProvisioningService#changeGameRole),
// which is why changeUserRole above refuses a blank role: revoking must be deliberate, never a default.
export const revokeGameAccess = async ({ accessToken, targetUserId, gameId }) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/access/change-role`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: { targetUserId, gameId, role: null }
        });
        return response.data;
    } catch (error) {
        console.error('Error revoking game access:', error.response?.data || error.message);
        throw error;
    }
};


// Additively grant an existing user a geo-restricted role on a game (coexists with whole-game access; owner-tier rejected).
export const provisionGeographyRole = async ({ accessToken, targetUserId, gameId, geographySegmentId, role }) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/access/provision-geography`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: { targetUserId, gameId, geographySegmentId, role }
        });
        return response.data;
    } catch (error) {
        console.error('Error provisioning geography role:', error.response?.data || error.message);
        throw error;
    }
};

// Remove one geography grant from a user on a game; leaves whole-game and other-geography grants intact.
export const revokeGeographyRole = async ({ accessToken, targetUserId, gameId, geographySegmentId }) => {
    try {
        const response = await axios({
            method: 'delete',
            url: `${config.serverBaseUrl}/v1/web/access/provision-geography`
                + `?targetUserId=${encodeURIComponent(targetUserId)}&gameId=${encodeURIComponent(gameId)}`
                + `&geographySegmentId=${encodeURIComponent(geographySegmentId)}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error revoking geography role:', error.response?.data || error.message);
        throw error;
    }
};

// Every userId that ALREADY has effective access at (or above) a scope — server folds the containment
// chain ORG > GAME: ORG = org owner + co-owners; GAME = + that game's owners/co-owners. The
// invite/grant wizard uses it to drop already-covered people from the member suggestions AND
// to block proceeding when the chosen member already has access. gameId required for GAME.
export const getAccessHolders = async ({ accessToken, scope, gameId }) => {
    try {
        const params = new URLSearchParams({ scope });
        if (gameId) params.set('gameId', gameId);
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/access/access-holders?${params.toString()}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching access holders:', error.response?.data || error.message);
        throw error;
    }
};

// Transfer SOLE ownership of an ORG or GAME to another member. The current owner is
// demoted to co-owner (keeps full access). Guarded server-side: only the current owner / org owner /
// super user may do this. gameId is required for scope='GAME'.
export const transferOwnership = async ({ accessToken, scope = 'ORG', targetUserId, gameId }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/access/transfer-owner`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: { scope, targetUserId, gameId }
        });
        return response.data;
    } catch (error) {
        console.error('Error transferring ownership:', error.response?.data || error.message);
        throw error;
    }
};

// Remove an Org Co-Owner from org ownership. They stay an org Member and become Game Co-Owner of each game in
// keepGameIds (any number, none included). Org ownership covers every game, so it is never narrowed in place. The
// org's primary owner is refused (409) - transfer ownership first.
export const removeOrgCoOwner = async ({ accessToken, targetUserId, keepGameIds = [] }) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/access/remove-org-co-owner`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: { targetUserId, keepGameIds }
        });
        return response.data;
    } catch (error) {
        console.error('Error removing org co-owner:', error.response?.data || error.message);
        throw error;
    }
};

export const createRole = async (obj) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/role`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            },
            data: obj.roleData
        });
        return response.data;
    } catch (error) {
        console.error('Error creating role:', error.response?.data || error.message);
        throw error;
    }
};

export const updateRole = async (obj) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/role/${obj.roleId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            },
            data: obj.roleData
        });
        return response.data;
    } catch (error) {
        console.error('Error updating role:', error.response?.data || error.message);
        throw error;
    }
};

export const deleteRole = async (obj) => {
    try {
        const response = await axios({
            method: 'delete',
            url: `${config.serverBaseUrl}/v1/web/role/${obj.roleId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting role:', error.response?.data || error.message);
        throw error;
    }
};